/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { $SimpleChanges } from '@ng-falcon/+legacy/+model/on-changes/simple-changes';
import { NgfOverflow } from '@ng-falcon/ngf-overflow/ngf-overflow';
import { NgfTooltipContent } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-content';
import { NgfTooltipService } from '@ng-falcon/ngf-tooltip/ngf-tooltip.service';

@Directive({
  selector: '[ngfOverflow]',
  standalone: true
})
export class NgfOverflowDirective implements OnInit, OnChanges, OnDestroy {

  @Input('ngfOverflow')
  overflow: NgfOverflow | ''; // empty string means default value

  @Input('ngfOverflowTooltip')
  tooltip: NgfTooltipContent | null = null;

  @Input('ngfOverflowTooltipOnHover')
  tooltipOnHover = true;
  @Input('ngfOverflowTooltipOnFocus')
  tooltipOnFocus = true;
  @Input('ngfOverflowTooltipDisabled')
  tooltipDisabled = false;

  protected isOpen = false;

  constructor(private elementRef: ElementRef<HTMLElement>,
              private renderer: Renderer2,
              private vcr: ViewContainerRef,
              private tooltipService: NgfTooltipService) { }

  ngOnInit(): void {
    const display = getComputedStyle(this.elementRef.nativeElement).display;

    if (display === 'inline') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'inline-block');
      this.renderer.setStyle(this.elementRef.nativeElement, 'vertical-align', 'bottom');
      this.renderer.setStyle(this.elementRef.nativeElement, 'max-width', '100%');
    }

    if (this.overflow === 'hidden' || this.overflow === '') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'overflow', 'hidden');
      this.renderer.setStyle(this.elementRef.nativeElement, 'white-space', 'nowrap');
      this.renderer.setStyle(this.elementRef.nativeElement, 'text-overflow', 'clip');
    } else if (this.overflow === 'wrap') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'overflow', 'hidden');
      this.renderer.setStyle(this.elementRef.nativeElement, 'white-space', 'normal');
      this.renderer.setStyle(this.elementRef.nativeElement, 'text-overflow', 'clip');
    } else if (this.overflow === 'ellipsis') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'overflow', 'hidden');
      this.renderer.setStyle(this.elementRef.nativeElement, 'white-space', 'nowrap');
      this.renderer.setStyle(this.elementRef.nativeElement, 'text-overflow', 'ellipsis');
    }

    if (!this.tooltipDisabled) {
      this.createTooltip();
    }
  }

  ngOnChanges(changes: $SimpleChanges<this>): void {
    if (changes.tooltipDisabled && !changes.tooltipDisabled.firstChange) {
      if (!this.tooltipDisabled) {
        this.createTooltip();
      } else {
        this.destroyTooltip();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyTooltip();
  }

  private createTooltip(): void {
    this.tooltipService.create(this.elementRef, {
      vcr: this.vcr,
      canOpen: () => !this.tooltipDisabled && this.doesContentOverflow(),
      content: () => this.tooltip ?? this.elementRef.nativeElement,
    }, {
      showOnHover: this.tooltipOnHover,
      showOnFocus: this.tooltipOnFocus
    });
  }

  private destroyTooltip(): void {
    this.tooltipService.destroy(this.elementRef);
  }

  private doesContentOverflow(): boolean {
    const elements: HTMLElement[] = [this.elementRef.nativeElement];

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      const computedElementStyles = getComputedStyle(element);

      if (computedElementStyles.display === 'inline') {
        // we have to skip inline elements, because they cannot be measured
        // also in Chrome "scrollWidth" is always 0
        // where in Firefox "scrollWidth" has same value as "offsetWidth"
        continue;
      }

      const lineClamp = computedElementStyles.getPropertyValue('-webkit-line-clamp');
      if (lineClamp && lineClamp !== 'none' && parseInt(lineClamp) > 1 && element.scrollHeight > element.parentElement.clientHeight) {
        return true;
      }

      if (element.offsetWidth !== element.scrollWidth) {
        return true;
      }

      if (element.children.length > 0) {
        elements.push(...Array.from(element.children) as HTMLElement[]);
      }
    }

    return false;
  }
}
